/* eslint-disable import/no-extraneous-dependencies */
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { OPTION_LIST } from 'constants/constants';
import { useOptionList } from 'hooks/useOptionList';
import { Title } from 'ui-library';
import { PAYMENT_PERIODS } from 'pages/utils';
import { ApplicationTypesEnum } from 'components/ApplicationDetails/Applications/Applications.types';
import ButtonRadioElement from 'components/AtomicStructure/atoms/ButtonRadioElement';
import { useStrategyRiskComparison } from 'datasource/useStrategyRiskComparison';
import RiskComparisonComp from 'components/RiskComparisonComp';
import ProductSummary from './components/ProductSummary/ProductSummary';
import './Finalization.css';

const Finalization = ({ datasource }) => {
    const { dfsClientId } = useParams();

    const {
        t,
        appsList,
        loadingAppsByContainer,
        languagesOption,
        passData,
        error,
        data,
        isFollowUpInvestorProfileCase,
    } = datasource({
        dfsClientId,
    });

    const { securityAllocations } = useStrategyRiskComparison({ fromFinalize: true, enabled: isFollowUpInvestorProfileCase || false });

    const {
        lists: [optionWithdrawalPeriodicity],
    } = useOptionList([OPTION_LIST.WithdrawalPeriodicity]);

    const getPeriodicityLabel = useCallback(
        (periodicity) => Object.values(PAYMENT_PERIODS).find((f) => f.id === periodicity)
            ?.label,
        [],
    );

    const getWithdrawalFrequencyLabel = useCallback(
        (frequency) => optionWithdrawalPeriodicity?.find((f) => f.id === frequency)?.label,
        [optionWithdrawalPeriodicity],
    );

    const getApplicationType = (app) => {
        if (
            app?.changePlan
            && app?.investmentDescription?.changeInvestmentAmount?.isSell
        ) {
            return ApplicationTypesEnum.SELL;
        }
        if (
            app?.changePlan
            && app?.investmentDescription?.changeInvestmentAmount
            && !app?.investmentDescription?.changeInvestmentAmount?.isSell
        ) {
            return ApplicationTypesEnum.BUY;
        }

        if (
            app?.changePlan
            && !app?.investmentDescription?.changeInvestmentAmount
        ) {
            return ApplicationTypesEnum.CHANGE;
        }

        return ApplicationTypesEnum.NEW;
    };

    const listOfApplications = useMemo(
        () => (appsList?.length > 0
            ? appsList?.filter((application) => application?.status !== 2)
            : []),
        [appsList],
    );
    const newApps = useMemo(
        () => listOfApplications?.filter(
            (app) => getApplicationType(app) === ApplicationTypesEnum.NEW,
        ),
        [listOfApplications, getApplicationType],
    );
    const changeApps = useMemo(
        () => listOfApplications?.filter(
            (app) => getApplicationType(app) === ApplicationTypesEnum.CHANGE,
        ),
        [listOfApplications, getApplicationType],
    );
    const buyApps = useMemo(
        () => listOfApplications?.filter(
            (app) => getApplicationType(app) === ApplicationTypesEnum.BUY,
        ),
        [listOfApplications, getApplicationType],
    );
    const sellApps = useMemo(
        () => listOfApplications?.filter(
            (app) => getApplicationType(app) === ApplicationTypesEnum.SELL,
        ),
        [listOfApplications, getApplicationType],
    );

    const sortedListOfApplications = [
        ...newApps,
        ...changeApps,
        ...buyApps,
        ...sellApps,
    ];

    return (
        <div className="new-portfolio quality-check">
            <Title type={1} className="font-normal">
                {t(
                    'portfolio.createNewPortfolio.opening.finalizeTheApplication',
                )}
            </Title>
            <div className="select-document-language">
                <ButtonRadioElement
                    data-cy="documentLanguage"
                    label={t(
                        'portfolio.createNewPortfolio.opening.documentLanguage',
                    )}
                    items={languagesOption}
                    error={error?.documentLanguage}
                    passData={passData}
                    value={data?.documentLanguage}
                    nameData="documentLanguage"
                    wrapperClassName="buttonRadioElementWrapper"
                />
            </div>

            {isFollowUpInvestorProfileCase
                ? (
                    <div className="strategy-risk-comparison-container mt-32">
                        <RiskComparisonComp
                            chartData={securityAllocations?.beforeOptimization}
                            title={
                                t('StrategyRiskComparison.Title.BeforeOptimization')
                            }
                            subTitle={
                                t('StrategyRiskComparison.SubTitle.BeforeOptimization')
                            }
                        />
                        <RiskComparisonComp
                            chartData={securityAllocations?.afterOptimization}
                            title={
                                t('StrategyRiskComparison.Title.AfterOptimization')
                            }
                            subTitle={
                                t('StrategyRiskComparison.SubTitle.AfterOptimization')
                            }
                        />
                    </div>
                ) : (
                    <ProductSummary
                        t={t}
                        loadingAppsByContainer={loadingAppsByContainer}
                        sortedListOfApplications={sortedListOfApplications}
                        getPeriodicityLabel={getPeriodicityLabel}
                        getWithdrawalFrequencyLabel={getWithdrawalFrequencyLabel}
                        getApplicationType={getApplicationType}
                    />
                )}
        </div>
    );
};

export default Finalization;
